import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const VisiGiftRules = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sections = [
    {
      id: 'how-it-works',
      title: 'How VisiGift Works',
      content: (
        <div className="space-y-3 text-sm">
          <p>VisiGift is a rewarding ad platform where users earn points by engaging with ads, landing on the home page, and clicking interactive buttons. Points can be redeemed for discounts on the Think5Whys shopping platform (e.g., 100 points = ₹10 off).</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Land on the VisiGift home page daily to earn bonus points (up to 50 for streaks).</li>
            <li>View short ads (15-30 seconds) or videos for 5-25 points per completion.</li>
            <li>Click buttons (e.g., "View Ad", "Shop Now") for 5-10 points each.</li>
            <li>All interactions are voluntary and opt-in only.</li>
          </ul>
          <p>Users must complete the full interaction without skipping to qualify for points. Points are non-transferable and valid for 12 months from earning.</p>
        </div>
      )
    },
    {
      id: 'earning-points',
      title: 'Earning Points Rules',
      content: (
        <div className="space-y-3 text-sm">
          <p>Points are awarded based on verified engagement:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Full ad/video view: 5-25 points (non-skippable during playback).</li>
            <li>Button clicks: 5-10 points (must lead to genuine interaction).</li>
            <li>Daily landing bonus: 10-50 points (increases with streaks; resets after 7 missed days).</li>
            <li>Referrals: 100 points per valid invite (verified account creation and first engagement).</li>
          </ul>
          <p>Random bonuses may apply (e.g., "chance to double points"), with odds disclosed upfront (e.g., 20% chance). No guarantees on point amounts or availability. Fraudulent activity (e.g., bots, multiple accounts) results in point forfeiture and account suspension.</p>
        </div>
      )
    },
    {
      id: 'redemption',
      title: 'Redemption Rules',
      content: (
        <div className="space-y-3 text-sm">
          <p>Redeem points for discounts on Think5Whys:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Minimum 50 points to redeem.</li>
            <li>Points equivalent: 100 points = ₹10 off (capped at 25% of order value).</li>
            <li>Applicable only to eligible products; exclusions apply (e.g., gift cards, digital goods).</li>
            <li>One redemption per order; unused points do not roll over.</li>
            <li>Rewards are non-refundable, non-exchangeable, and expire after 12 months.</li>
          </ul>
          <p>VisiGift/Think5Whys reserves the right to verify redemptions and deny if suspicious activity is detected.</p>
        </div>
      )
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      content: (
        <div className="space-y-3 text-sm">
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Opt-in affirmatively for each interaction (e.g., tap "Watch Now").</li>
            <li>Complete full ad/view without skipping or closing early.</li>
            <li>Use one account per user; multiple accounts prohibited.</li>
            <li>Provide accurate information; false details lead to disqualification.</li>
            <li>Comply with all applicable laws; no sharing login credentials.</li>
            <li>Report issues within 7 days; unresolved claims not honored.</li>
          </ul>
          <p>Users agree to receive personalized ads based on activity. Opt-out available in settings, but may reduce point opportunities.</p>
        </div>
      )
    },
    {
      id: 'prohibitions',
      title: 'Prohibitions & Restrictions',
      content: (
        <div className="space-y-3 text-sm">
          <p>The following are strictly prohibited:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Fraud, bots, scripts, or automated tools to earn points.</li>
            <li>Sharing, selling, or transferring points/accounts.</li>
            <li>Viewing ads in background or using ad blockers.</li>
            <li>Encouraging others to engage fraudulently (e.g., paid referrals).</li>
            <li>Abusing system (e.g., excessive claims, disputes without merit).</li>
            <li>Direct monetary rewards (points are indirect discounts only).</li>
          </ul>
          <p>Violations result in immediate account termination, point forfeiture, and potential legal action. VisiGift monitors for abuse using analytics.</p>
        </div>
      )
    },
    {
      id: 'company-rights',
      title: 'VisiGift Rights & Disclaimers',
      content: (
        <div className="space-y-3 text-sm">
          <p>VisiGift (Think5Whys) reserves the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Modify rules, point values, or program at any time without notice.</li>
            <li>Terminate/suspend accounts for violations or at discretion.</li>
            <li>Withhold points/rewards if funding unavailable or disputes arise.</li>
            <li>Use user data for personalization, analytics (anonymized), and improvements.</li>
            <li>Not guarantee ad availability, point earnings, or redemption success.</li>
          </ul>
          <p>NO WARRANTIES: Program provided "as is." No liability for lost points, technical issues, or indirect damages. Indemnification: Users indemnify VisiGift against claims from misuse. Governing Law: Laws of Maharashtra, India. Disputes resolved in Thane courts.</p>
        </div>
      )
    },
    {
      id: 'contact',
      title: 'Contact & Support',
      content: (
        <div className="space-y-3 text-sm text-center">
          <p>Questions? Email <a href="mailto:support@visigift.com" className="text-purple-400 hover:underline">support@visigift.com</a></p>
          <p>Effective Date: November 10, 2025. Last Updated: November 10, 2025.</p>
          <Link to="/visigift-home" className="inline-block bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors">
            Back to VisiGift
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#1a0b1e] text-purple-100 font-sans">
      {/* Header */}
      <header className="bg-[#2a0f2f] shadow-sm border-b border-purple-800 px-4 py-3 sticky top-0 z-10">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-xl font-bold text-purple-400">VisiGift Rules</h1>
          <p className="text-sm text-purple-300 mt-1">Terms & Conditions for Rewarding Ads</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {sections.map((section) => (
            <section key={section.id} className="bg-gray-700 rounded-lg shadow-sm border border-purple-800 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-2 text-left font-semibold text-yellow-200 bg-black hover:bg-purple-700 transition-colors"
              >
                <span>{section.title}</span>
                <span className={`text-sm transition-transform ${expandedSections[section.id] ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              {expandedSections[section.id] && (
                <div className="p-2 border-t border-purple-700/30">
                  {section.content}
                </div>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
     <footer className="bg-[#2a0f2f] border-t border-purple-800 text-purple-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-purple-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-purple-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-purple-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-purple-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VisiGiftRules;